import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Typography from "@material-ui/core/Typography";
import FormHelperText from "@material-ui/core/FormHelperText";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { styled } from "@mui/material/styles";

import styles from "assets/jss/material-dashboard-pro-react/components/customInputStyle.js";

const useStyles = makeStyles(styles);

const exceptionList = ["additional_insured", "mname"];

const getColorByValue = (required, value, name, error) => {
  value = String(value ?? "").trim();
  if (!required) {
    return "#888888";
  }
  if (error || (value === "" && !exceptionList.includes(name))) {
    return "red";
  }
  if (required && value) {
    return "#008670";
  }
};

const CustomInputStyled = styled(OutlinedInput)(
  ({ theme, error, required, value, mergedInputProps, inputProps }) => (
    {
    "&.MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: getColorByValue(required, value, mergedInputProps?.name, error),
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: getColorByValue(required, value, mergedInputProps?.name, error),
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: getColorByValue(required, value, mergedInputProps?.name, error),
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: inputProps?.type === "checkbox" ? "none" : undefined,
    },
  })
);

export default function CustomInput(props) {
  const classes = useStyles();
  const {
    formControlProps,
    labelText,
    id,
    labelProps,
    inputProps,
    InputProps,
    error,
    white,
    inputRootCustomClasses,
    success,
    helperText,
    rtlActive,
    secondaryText,
    changeFunction,
    inputRef,
  } = props;

  const mergedInputProps = {
    ...inputProps,
    ...InputProps?.mergedInputProps,
  };

  const labelClasses = classNames({
    [classes.labelRootError]: error,
    [classes.labelRootSuccess]: success && !error,
    [classes.labelRTL]: rtlActive,
  });

  const formControlClasses = classNames(
    formControlProps?.className,
    classes.formControl
  );

  const inputClasses = classNames({
    [classes.input]: true,
    [classes.whiteInput]: white,
  });
  const today = new Date().toISOString().split("T")[0];

  return (
    <FormControl
      {...formControlProps}
      className={formControlClasses}
      variant="outlined"
    >
      {labelText && (
        <Typography component="pre">
          <InputLabel
            className={classNames(
              classes.labelRoot,
              classes.labelOutlined,
              labelClasses
            )}
            htmlFor={id}
            {...labelProps}
            shrink
          >
            {labelText}
          </InputLabel>
        </Typography>
      )}
      {secondaryText && (
        <FormHelperText
          id={`${id}-secondaryText`}
          className={classNames(classes.secondaryText, classes.labelOutlined)}
        >
          {secondaryText}
        </FormHelperText>
      )}
      <CustomInputStyled
        id={id}
        {...InputProps}
        multiline={mergedInputProps?.multiline}
        rows={mergedInputProps?.rows}
        required={mergedInputProps?.required}
        value={props.value ? props.value : mergedInputProps?.value}
        onChange={props.onChange ? props.onChange : mergedInputProps?.onChange}
        inputRef={inputRef || InputProps?.ref}
        error={
          (mergedInputProps?.required && !mergedInputProps?.value) ||
          mergedInputProps?.alwaysRed
            ? true
            : false
        }
        inputProps={{
          ...mergedInputProps,
          min: mergedInputProps?.type === "date" ? today : undefined,
          max: mergedInputProps?.max ? mergedInputProps?.max : undefined,
        }}
        classes={{
          input: inputClasses,
          root: inputRootCustomClasses,
          disabled: classes.disabled,
        }}
      />
      {helperText && (
        <FormHelperText
          id={`${id}-text`}
          className={classNames({
            [classes.labelRootError]: error,
            [classes.labelRootSuccess]: success && !error,
          })}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

CustomInput.propTypes = {
  labelText: PropTypes.node,
  labelProps: PropTypes.object,
  id: PropTypes.string.isRequired,
  mergedInputProps: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
  }),
  formControlProps: PropTypes.object,
  inputRootCustomClasses: PropTypes.string,
  error: PropTypes.bool,
  success: PropTypes.bool,
  white: PropTypes.bool,
  helperText: PropTypes.node,
  rtlActive: PropTypes.bool,
  secondaryText: PropTypes.node,
};
